<script setup>
import VSwatches from "vue3-swatches";
import TD from "@/Components/Table/Element.vue";
import SettingsLayout from "@/Layouts/Settings.vue";
import Input from "@/Components/Settings/Input.vue";
import Textarea from "@/Components/Textarea.vue";
import Select from "@/Components/Select.vue";
import Label from "@/Components/Settings/Label.vue";
import HeaderLink from "@/Components/Settings/HeaderLink.vue";
import HeaderButton from "@/Components/Settings/HeaderButton.vue";
import Heading from "@/Components/Settings/Heading.vue";
import { Head, useForm } from "@inertiajs/inertia-vue3";
import { Inertia } from "@inertiajs/inertia";
import MultiSelect from "@/Components/MultiSelect.vue";
import { onUnmounted } from "vue";

import { swatches } from "@/shared/utils";

const page_name = "Behandlungstypen";

const props = defineProps({
  errors: Object,
  rooms: Array,
  practitioners: Array,
  interval: Number,
  interval_values: Object,
});
let saveBtnClicked = false;

//onUnmounted(
//    Inertia.on("before", (e) => {
//        if (form.isDirty && !saveBtnClicked) {
//            return confirm(
//                "Sie haben ungespeicherte Eingaben. Wollen Sie diese Seite wirklich verlassen?"
//            );
//        }
//    })
//);

function submitForm() {
  saveBtnClicked = true;
  form.post("/types");
}

const form = useForm({
  name: null,
  materials: null,
  abbreviation: null,
  room: [],
  practitioner: [],
  duration: 1,
  color: "#00cc8b",
  widgetable: false,
});

function pracitionerChange(e) {
  form.practitioner = e;
}

function roomChange(e) {
  form.room = e;
}
</script>

<template>
  <SettingsLayout>
    <!-- <Head title="Stammdaten - Grundeinstellungen" /> -->
    <Head :title="`${page_name} - Grundeinstellungen`" />

    <template #breadcrumb>{{ page_name }}</template>
    <template #controls>
      <HeaderLink
        class="mr-3"
        href="/einstellungen/behandlungstypen"
        type="close"
        >Zurück</HeaderLink
      >
      <HeaderButton @click="submitForm()">Speichern</HeaderButton>
    </template>
    <Heading title="Neuen Behandlungstypen anlegen" />

    <p class="text-sm text-oteoText mb-3">
      Bevor Sie Termine anlegen können, müssen Sie die Behandlungstypen anlegen,
      z. B. PZR, Kontrolle usw. Wählen Sie dazu bitte einen Zeitraum, einen
      Behandlungsraum und falls für die Behandlung nur ein bestimmter
      Behandler/-in in Frage kommt, einen Behandler/-in aus.
    </p>

    <p
      v-if="$page.props.auth.office_canusewidget"
      class="text-sm text-oteoText mb-3"
    >
      Wenn Sie die Online-Terminvergabe auf Ihrer Praxis-Homepage nutzen, können
      Sie geeignete Behandlungstypen für die Online-Terminvergabe freigeben.
      Diese Termine können dann von Ihren Patient/-innen über Ihre
      Praxis-Homepage gebucht werden.
    </p>

    <p
      v-if="$page.props.auth.office_canusewidget"
      class="text-sm text-oteoText"
    >
      Wie Sie die Online-Terminvergabe in Ihre Praxis-Homepage integrieren,
      erfahren Sie unter dem Punkt
      <a class="text-oteo-300" href="/einstellungen/widgeteinbindung"
        >„Online-Terminvergabe / Einbindung“</a
      >.
    </p>

    <div class="grid grid-cols-2 max-w-3xl gap-y-8 gap-5 mt-8">
      <div class="">
        <Label> Benennung </Label>

        <Input class="w-full" v-model="form.name" type="text" />
        <div class="mt-2 text-red-600 text-sm" v-if="errors.name">
          {{ errors.name }}
        </div>
      </div>

      <div class="row-span-3 flex flex-col">
        <Label> Werkzeuge / Materialien </Label>
        <Textarea
          placeholder="Bitte eintragen"
          class="w-full h-full resize-none"
          v-model="form.materials"
        ></Textarea>
        <div class="mt-2 text-red-600 text-sm" v-if="errors.materials">
          {{ errors.materials }}
        </div>
      </div>
      <div class="">
        <Label> Kürzel (max. 3 Zeichen) </Label>

        <Input
          maxlength="3"
          v-model="form.abbreviation"
          class="w-full"
          type="text"
        />
        <div class="mt-2 text-red-600 text-sm" v-if="errors.abbreviation">
          {{ errors.abbreviation }}
        </div>
      </div>

      <div class="">
        <Label> Mögliche Behandlungsräume </Label>

        <MultiSelect
          @valueChange="roomChange"
          :options="
            rooms.map((r) => {
              return {
                value: r.id,
                text: r.name,
                label: r.number,
                selected: false,
              };
            })
          "
        />

        <!-- <Select v-model="form.room" class="w-full">
          <option :value="-1">Alle</option>
          <option v-for="room in rooms" :key="room.id" :value="room.id">
            {{ `${room.name}` }}
          </option>
        </Select> -->
        <div class="mt-2 text-red-600 text-sm" v-if="errors.room">
          {{ errors.room }}
        </div>
      </div>

      <div class="">
        <Label> Behandler </Label>

        <!-- <Select v-model="form.practitioner" class="w-full">
          <option :value="-1">Alle</option>
          <option
            v-for="practitioner in practitioners"
            :key="practitioner.id"
            :value="practitioner.id"
          >
            {{ `${practitioner.firstname} ${practitioner.lastname}` }}
          </option>
        </Select> -->

        <MultiSelect
          @valueChange="pracitionerChange"
          :options="
            practitioners.map((p) => {
              return {
                value: p.id,
                text: p.firstname + ' ' + p.lastname,
                label: p.username,
                selected: false,
              };
            })
          "
        />

        <div class="mt-2 text-red-600 text-sm" v-if="errors.practitioner">
          {{ errors.practitioner }}
        </div>
      </div>

      <div class="">
        <Label> Farbkennung </Label>
        <VSwatches :swatches="swatches" v-model="form.color" />
        <div class="mt-2 text-red-600 text-sm" v-if="errors.color">
          {{ errors.color }}
        </div>
      </div>

      <div class="">
        <Label> Dauer </Label>

        <Select v-model="form.duration" class="w-full">
          <!--<option v-for="n in 10" :key="n" :value="n">
                        {{ n * interval }}-{{ n }} Min.
                    </option>-->
          <option v-for="(iv, n) in interval_values" :key="n" :value="n">
            {{ iv }} Min.
          </option>
        </Select>
        <div class="mt-2 text-red-600 text-sm" v-if="errors.duration">
          {{ errors.duration }}
        </div>
      </div>

      <div v-if="$page.props.auth.office_canusewidget" class="">
        <Label> Online-Terminvergabe </Label>
        <div class="flex max-w-3xl gap-5 mt-4">
          <Label class="mr-4 inline-block"
            >Behandlungstyp für Online-Terminvergabe freigeben</Label
          >
          <input v-model="form.widgetable" type="checkbox" />
        </div>
        <div class="mt-2 text-red-600 text-sm" v-if="errors.widgetable">
          {{ errors.widgetable }}
        </div>
      </div>
    </div>
  </SettingsLayout>
</template>
